/**
 * Sign in with OpenMarkets — the ONE shared SDK instance for the whole app.
 *
 * A singleton rather than the SDK's React hook because every request needs `getApiKey()`: access
 * keys last 7 days and renew silently from a rotating refresh token. Two instances refreshing the
 * same session would present a retired refresh token, which the server reads as theft and
 * answers by revoking the install — so there is exactly one, and every key read goes through its
 * single-flight `getApiKey()`. Never cache a key.
 */
import { useSyncExternalStore } from "react";
import {
  createOpenMarketsAuth,
  hasCallbackParams,
  type OpenMarketsSession,
  type SignInResult,
} from "@openmarketsai/app-sdk";

/** The marketplace app's slug. */
export const CLIENT_ID = "omodds";
/** Optional scope: live prices over the WebSocket. Without it the board polls. */
export const STREAM_SCOPE = "stream:realtime";
/**
 * Optional scope, on top of `stream:realtime`: the low-latency `ticks` channel. Without it the
 * stream falls back to `liquidity` deltas.
 */
export const TICKS_SCOPE = "stream:ticks";

type Auth = ReturnType<typeof createOpenMarketsAuth>;
let instance: Auth | null = null;

function auth(): Auth {
  if (!instance) {
    instance = createOpenMarketsAuth({
      clientId: CLIENT_ID,
      // Exact-match redirect URI — the trailing slash matters.
      redirectUri: window.location.origin + "/",
      apiBase: import.meta.env.VITE_OPENMARKETS_BASE || undefined,
      consoleBase: import.meta.env.VITE_OPENMARKETS_CONSOLE || undefined,
    });
  }
  return instance;
}

function readStoredSession(): OpenMarketsSession | null {
  if (typeof window === "undefined") return null;
  try {
    return auth().getSession();
  } catch {
    return null;
  }
}

let session: OpenMarketsSession | null = readStoredSession();
const listeners = new Set<() => void>();

function setSession(next: OpenMarketsSession | null): void {
  session = next;
  for (const l of listeners) l();
}

export function subscribeAuth(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

export function useSession(): OpenMarketsSession | null {
  return useSyncExternalStore(subscribeAuth, () => session, () => null);
}

/** Stable per install — a silent key refresh keeps it; a different account changes it. */
export function identity(): string | null {
  return session ? session.installId : null;
}

export function hasScope(scope: string): boolean {
  return session?.scopes.includes(scope) ?? false;
}

/**
 * The key for the NEXT request. `null` means the session is dead (refresh token expired,
 * revoked or reused), so we sign out and the landing page takes over. A network failure
 * throws instead, and must not sign anyone out.
 */
export async function getApiKey(): Promise<string | null> {
  if (!session) return null;
  const key = await auth().getApiKey();
  if (!key) signOut();
  return key;
}

/**
 * Opens the consent popup (the SDK falls back to a redirect if it is blocked) and RESOLVES with
 * the outcome. Nothing throws: `denied` — a cancel, or a closed window — is a normal result.
 */
export async function signIn(): Promise<SignInResult> {
  const r = await auth().signIn();
  if (r.status === "success") setSession(r.session);
  return r;
}

function safeHasCallback(): boolean {
  try {
    return hasCallbackParams();
  } catch {
    return false;
  }
}

/** True while this page load carries a sign-in response to complete. */
export function pendingCallback(): boolean {
  return typeof window !== "undefined" && safeHasCallback();
}

/**
 * Completes a sign-in on page load. Inside the sign-in popup the SDK reports to the opener and
 * closes the window itself; after a redirect-mode return it stores the session here.
 */
export async function completeSignIn(): Promise<SignInResult | null> {
  if (!pendingCallback()) return null;
  const r = await auth().handleCallback();
  if (r.status === "success") setSession(r.session);
  return r;
}

/** Local sign-out. Revoking the install is the user's call, from their OpenMarkets dashboard. */
export function signOut(): void {
  try {
    void auth().signOut();
  } catch {
    // Already signed out.
  }
  setSession(null);
}
